import i18n from "@/i18n";
/**
 * VuosilomaBasisDialog — VUOSILOMA-V1. Effective-dated Employment
 * Terms revision of the contractual annual-leave earning rule via the
 * canonical terms endpoint (same Close locks as every Terms change).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import {
    VL_BASIS_FIRST_OF_MONTH_MSG, basisChangeNotFirstOfMonth, termsRevisionErrorMessage,
} from "./workforceHelpers";
import { VL_BASIS_OPTIONS } from "./vuosilomaHelpers";

export default function VuosilomaBasisDialog({
    open, onOpenChange, employeeId, restaurantId, currentBasis, onSaved,
}) {
    const [basis, setBasis] = useState("");
    const [effectiveFrom, setEffectiveFrom] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) { setBasis(currentBasis || ""); setEffectiveFrom(""); setError(""); }
    }, [open, currentBasis]);

    const notFirstOfMonth = basisChangeNotFirstOfMonth(currentBasis, basis, effectiveFrom);

    const save = async () => {
        setPending(true);
        setError("");
        try {
            await api.post(`/workforce/employees/${employeeId}/employment-terms`,
                { effective_from: effectiveFrom, vuosiloma_earning_basis: basis },
                { params: { restaurant_id: restaurantId } });
            toast.success(i18n.t("wf.vuosilomaBasisDialog.earningRuleSaved", "Earning rule saved"));
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            setError(termsRevisionErrorMessage(err));
        } finally {
            setPending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="vl-basis-dialog" className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{i18n.t("wf.vuosilomaBasisDialog.annualLeaveEarningRule", "Annual-leave earning rule")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("wf.vuosilomaBasisDialog.setByTheEmploymentContractTes", "Set by the employment contract (TES §27). The 14-day rule applies only when the contract gives at least 14 working days in every month.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                    <Select value={basis} onValueChange={setBasis}>
                        <SelectTrigger data-testid="vl-basis-select">
                            <SelectValue placeholder={i18n.t("wf.vuosilomaBasisDialog.select", "Select…")} />
                        </SelectTrigger>
                        <SelectContent>
                            {VL_BASIS_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}
                                    data-testid={`vl-basis-opt-${o.value}`}>{o.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <label className="block">
                        <span className="text-neutral-700">{i18n.t("wf.vuosilomaBasisDialog.effectiveFrom", "Effective from")}</span>
                        <Input type="date" className="mt-1" value={effectiveFrom}
                            data-testid="vl-basis-effective-from"
                            onChange={(e) => setEffectiveFrom(e.target.value)} />
                        <span data-testid="vl-basis-first-of-month-hint"
                            className="mt-1 block text-xs text-neutral-500">
                            {i18n.t("wf.workforceHelpers.VL_BASIS_FIRST_OF_MONTH_MSG", VL_BASIS_FIRST_OF_MONTH_MSG)}
                        </span>
                    </label>
                    {notFirstOfMonth && (
                        <div role="alert" data-testid="vl-basis-first-of-month-error"
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                            {i18n.t("wf.workforceHelpers.VL_BASIS_FIRST_OF_MONTH_MSG", VL_BASIS_FIRST_OF_MONTH_MSG)}
                        </div>
                    )}
                    {error && (
                        <div role="alert" data-testid="vl-basis-error"
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                            {error}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        data-testid="vl-basis-cancel">{i18n.t("common.cancel", "Cancel")}</Button>
                    <Button onClick={save} disabled={pending || !basis || !effectiveFrom || notFirstOfMonth}
                        data-testid="vl-basis-save">{pending ? i18n.t("common.saving", "Saving…") : i18n.t("common.save", "Save")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
